import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { IconButton, Tooltip } from "@mui/material";
import { KPISummaryItem, KPISummaryRequest } from "api";
import { useKPISummary } from "hooks";
import { DefaultTable, Loading } from "components";
import { EsgScorecardIcon } from "assets";
import { CustomKpisSummaryTableColumns } from "./columns";

export const CustomKpisSummaryTable = ({
    query,
    onScorecardIconClick,
    onRefetchReady
}: {
    query: KPISummaryRequest,
    onScorecardIconClick: (row: KPISummaryItem) => void,
    onRefetchReady?: (refetchFn: (query: any) => void) => void
}) => {
    const [currentQuery, setCurrentQuery] = useState<KPISummaryRequest>(query);
    const { data, isFetching } = useKPISummary(currentQuery);

    // Pass refetch function to parent on mount
    useEffect(() => {
        if (onRefetchReady) {
            onRefetchReady((updatedQuery) => {
                // Update query parameters with the updated query from parent
                setCurrentQuery(updatedQuery);
            });
        }
    }, [onRefetchReady]);

    if (isFetching) return <Box sx={{ display: 'flex', height: '300px', position: 'relative', padding: '24px 41px' }}>
        <Loading />
    </Box>

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, width: "100%", overflow: 'auto' }}>
            <DefaultTable
                data={data || []}
                columns={CustomKpisSummaryTableColumns}
                enableGrouping
                enableColumnDragging={false}
                enableRowActions
                displayColumnDefOptions={{
                    'mrt-row-actions': {
                        header: 'Actions',
                        size: 109,
                    },
                }}
                renderRowActions={({ row }) => (
                    <Box
                        sx={{
                            height: "45px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "109px",
                        }}
                    >
                        <Tooltip arrow placement="top" title="View Scorecard">
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onScorecardIconClick(row.original);
                                }}
                            >
                                <EsgScorecardIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
                muiTableContainerProps={{
                    sx: {
                        "& thead tr:first-of-type th:first-of-type > div": {
                            justifyContent: "center",
                        },
                        "& tbody tr td:first-of-type > div": {
                            paddingLeft: 0,
                            paddingRight: 0,
                            justifyContent: "center",
                        },
                        "& tbody tr td:first-of-type": {
                            justifyContent: "center",
                            textAlign: "center",
                            px: 0,
                        },
                        "& tbody tr:first-of-type td > p": {
                            display: "flex",
                            minWidth: "100%",
                            justifyContent: "center",
                        },
                        border: "1px solid #D9D9D9",
                        borderRadius: "10px",
                        width: "100%",
                        height: "calc(100vh - 450px)",
                        minHeight: "200px",
                        overflow: "auto",
                    },
                }}
                muiTableHeadCellProps={({ column }) => ({
                    sx: {
                        lineHeight: "30px",
                        color: "#054254",
                        fontWeight: 600,
                        fontSize: "14px",
                        justifyContent: "center !important",
                        "& .MuiBadge-root": {
                            display: "none",
                        },
                        ...(column.id === 'mrt-row-actions' && {
                            borderRight: "1px solid #D9D9D9 !important",
                            padding: "0 !important",
                        }),
                    },
                })}
                muiTableHeadRowProps={{
                    sx: {
                        minHeight: "36px",
                        backgroundColor: "#F8FAFC",
                    },
                }}
                muiTableBodyRowProps={{
                    sx: {
                        minHeight: "45px",
                        height: "fit-content",
                        backgroundColor: "white",
                    },
                }}
                muiTableBodyCellProps={({ column }) => ({
                    sx: {
                        ...(column.id === 'mrt-row-actions' && {
                            borderRight: "1px solid #D9D9D9 !important",
                        }),
                    },
                })}
            />
        </Box>
    )
}